/**
 * VaccineRequirementsPanel — "will this pet pass the vaccine gate, and if
 * not, fix it here."
 *
 * Mirrors the API's `checkVaccinePolicy` (apps/api/src/vaccines/policy_gate.ts)
 * on the client: for every REQUIRED policy in the booking's service category
 * FOR THE PET'S SPECIES (policies are per species — a cat is never asked for
 * DHPP, a dog never for FVRCP), the pet passes when it has a record with no
 * expiry, or one whose expiry +
 * the policy's grace period is on/after the visit date. Anything else is
 * listed with an inline "Record vaccination" form (pre-filled with today and
 * the vaccine's typical validity) and, for managers, a "Grant override".
 *
 * When the server has already rejected the booking (422 `vaccines_required`)
 * its `missing[]` is passed in as the authority so the panel shows exactly
 * what the API is complaining about, even if the client-side mirror drifts.
 */
import { type VaccineGateMissing, createPetVaccination, createVaccineOverride } from '@/lib/api';
import type { Vaccine, VaccinePolicy, VaccineServiceCategory } from '@/lib/api';
import type { PetVaccinationSummary, PetWithVaccinations } from '@/lib/types';
import { cn } from '@/lib/utils';
import { AlertTriangle, Check, Loader2, ShieldCheck, Syringe } from 'lucide-react';
import { useState } from 'react';

export type RequirementStatus = 'valid' | 'not_recorded' | 'expired' | 'overridden';

export interface VaccineRequirement {
  vaccineId: string;
  vaccineName: string;
  status: RequirementStatus;
  /** Latest expiry on file for this vaccine, when one exists. */
  expiresOn: string | null;
  typicalValidityMonths: number | null;
}

/** YYYY-MM-DD + N days, UTC arithmetic on a date-only string. */
export function addDays(yyyymmdd: string, days: number): string {
  const [y, m, d] = yyyymmdd.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return yyyymmdd;
  const t = Date.UTC(y, m - 1, d) + days * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

/** YYYY-MM-DD + N months (clamped to month end), UTC. */
export function addMonths(yyyymmdd: string, months: number): string {
  const [y, m, d] = yyyymmdd.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return yyyymmdd;
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0));
  target.setUTCDate(Math.min(d, lastDay.getUTCDate()));
  return target.toISOString().slice(0, 10);
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Pure evaluation — exported so the wizard can gate "Continue" copy and the
 * review step can count outstanding issues without rendering.
 */
export function evaluateRequirements(input: {
  pet: PetWithVaccinations;
  policies: VaccinePolicy[];
  vaccines: Vaccine[];
  visitDate: string;
  serverMissing?: VaccineGateMissing[] | null;
  overriddenVaccineIds?: ReadonlySet<string>;
}): VaccineRequirement[] {
  const { pet, policies, vaccines, visitDate } = input;
  const nameOf = (id: string) =>
    vaccines.find((v) => v.id === id)?.name ??
    pet.vaccinations.find((v) => v.vaccineId === id)?.vaccineName ??
    id;
  const validityOf = (id: string) =>
    vaccines.find((v) => v.id === id)?.typicalValidityMonths ?? null;
  const serverById = new Map((input.serverMissing ?? []).map((m) => [m.vaccineId, m]));
  const overridden = input.overriddenVaccineIds ?? new Set<string>();

  const required = policies.filter((p) => p.required === 1 && p.species === pet.species);
  // A server complaint about a vaccine with no client-visible policy still
  // needs a row — the server is the authority.
  const vaccineIds = new Set<string>(required.map((p) => p.vaccineId));
  for (const m of serverById.keys()) vaccineIds.add(m);

  return [...vaccineIds].map((vaccineId) => {
    const policy = required.find((p) => p.vaccineId === vaccineId);
    const rows = pet.vaccinations.filter((v) => v.vaccineId === vaccineId);
    const latestExpiry =
      rows
        .map((r) => r.expiresOn)
        .filter((x): x is string => Boolean(x))
        .sort()
        .at(-1) ?? null;
    const server = serverById.get(vaccineId);
    const vaccineName = server?.vaccineName ?? nameOf(vaccineId);

    if (overridden.has(vaccineId)) {
      return {
        vaccineId,
        vaccineName,
        status: 'overridden',
        expiresOn: latestExpiry,
        typicalValidityMonths: validityOf(vaccineId),
      };
    }

    let status: RequirementStatus;
    if (server) {
      status = server.reason === 'not_recorded' || rows.length === 0 ? 'not_recorded' : 'expired';
    } else {
      const grace = policy?.gracePeriodDays ?? 0;
      const ok = rows.some((r) => !r.expiresOn || addDays(r.expiresOn, grace) >= visitDate);
      status = ok ? 'valid' : rows.length === 0 ? 'not_recorded' : 'expired';
    }
    return {
      vaccineId,
      vaccineName,
      status,
      expiresOn: latestExpiry,
      typicalValidityMonths: validityOf(vaccineId),
    };
  });
}

interface Props {
  pet: PetWithVaccinations;
  category: VaccineServiceCategory;
  /** Policies for `category` (any `required` flag — the panel filters). */
  policies: VaccinePolicy[];
  vaccines: Vaccine[];
  /** YYYY-MM-DD — the first day of the visit. */
  visitDate: string;
  /** From a 422 `vaccines_required` response, when the server has spoken. */
  serverMissing?: VaccineGateMissing[] | null;
  canRecord: boolean;
  canOverride: boolean;
  onVaccinationRecorded: (petId: string, summary: PetVaccinationSummary) => void;
  onOverrideGranted?: (petId: string, vaccineId: string) => void;
}

export function VaccineRequirementsPanel({
  pet,
  category,
  policies,
  vaccines,
  visitDate,
  serverMissing = null,
  canRecord,
  canOverride,
  onVaccinationRecorded,
  onOverrideGranted,
}: Props) {
  const [overridden, setOverridden] = useState<Set<string>>(() => new Set());
  const [openForm, setOpenForm] = useState<{
    vaccineId: string;
    kind: 'record' | 'override';
  } | null>(null);

  const requirements = evaluateRequirements({
    pet,
    policies,
    vaccines,
    visitDate,
    serverMissing,
    overriddenVaccineIds: overridden,
  });
  if (requirements.length === 0) return null;

  const failing = requirements.filter((r) => r.status === 'not_recorded' || r.status === 'expired');
  const allGood = failing.length === 0;

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        allGood
          ? 'border-border bg-background'
          : 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30',
      )}
      data-testid={`vaccine-panel-${pet.id}`}
    >
      <div className="mb-2 flex items-center gap-2">
        {allGood ? (
          <ShieldCheck className="size-4 text-success" strokeWidth={1.75} />
        ) : (
          <AlertTriangle className="size-4 text-amber-600 dark:text-amber-400" strokeWidth={1.75} />
        )}
        <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Vaccinations required for {category} ({pet.species}s)
        </p>
      </div>
      {!allGood && (
        <p className="mb-2 text-sm text-amber-900 dark:text-amber-200">
          {pet.name} cannot be booked until{' '}
          {failing.length === 1
            ? `${failing[0]?.vaccineName} is recorded`
            : 'these vaccinations are recorded'}
          {canOverride ? ' or a manager grants an override' : ''}.
        </p>
      )}
      <ul className="space-y-1.5">
        {requirements.map((r) => {
          const isOpen = openForm?.vaccineId === r.vaccineId;
          const failingRow = r.status === 'not_recorded' || r.status === 'expired';
          return (
            <li key={r.vaccineId} className="text-sm">
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <StatusChip status={r.status} />
                  <span className="min-w-0 break-words font-medium">{r.vaccineName}</span>
                  {r.expiresOn && (
                    <span className="num text-xs text-muted-foreground">
                      {r.status === 'expired' ? 'expired' : 'expires'} {r.expiresOn}
                    </span>
                  )}
                </div>
                {failingRow && !isOpen && (
                  <span className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
                    {canRecord && (
                      <button
                        type="button"
                        onClick={() => setOpenForm({ vaccineId: r.vaccineId, kind: 'record' })}
                        className="flex min-h-9 items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground press sm:min-h-0"
                      >
                        <Syringe className="size-3" strokeWidth={2} />
                        Record vaccination
                      </button>
                    )}
                    {canOverride && (
                      <button
                        type="button"
                        onClick={() => setOpenForm({ vaccineId: r.vaccineId, kind: 'override' })}
                        className="min-h-9 rounded-md border border-amber-400 px-2.5 py-1 text-xs font-medium text-amber-800 dark:text-amber-200 press sm:min-h-0"
                      >
                        Grant override
                      </button>
                    )}
                  </span>
                )}
              </div>
              {isOpen && openForm.kind === 'record' && (
                <RecordForm
                  petId={pet.id}
                  requirement={r}
                  onCancel={() => setOpenForm(null)}
                  onRecorded={(summary) => {
                    setOpenForm(null);
                    onVaccinationRecorded(pet.id, summary);
                  }}
                />
              )}
              {isOpen && openForm.kind === 'override' && (
                <OverrideForm
                  petId={pet.id}
                  requirement={r}
                  visitDate={visitDate}
                  onCancel={() => setOpenForm(null)}
                  onGranted={() => {
                    setOpenForm(null);
                    setOverridden((prev) => new Set(prev).add(r.vaccineId));
                    onOverrideGranted?.(pet.id, r.vaccineId);
                  }}
                />
              )}
            </li>
          );
        })}
      </ul>
      {!allGood && !canRecord && !canOverride && (
        <p className="mt-2 text-xs text-muted-foreground">
          You don't have permission to record vaccinations — ask a manager, or record it from the
          pet's page.
        </p>
      )}
    </div>
  );
}

function StatusChip({ status }: { status: RequirementStatus }) {
  const map: Record<RequirementStatus, { label: string; cls: string }> = {
    valid: { label: 'Current', cls: 'bg-success/10 text-success' },
    overridden: { label: 'Override', cls: 'bg-primary/10 text-primary' },
    not_recorded: { label: 'Missing', cls: 'bg-destructive/10 text-destructive' },
    expired: { label: 'Expired', cls: 'bg-destructive/10 text-destructive' },
  };
  const { label, cls } = map[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
        cls,
      )}
    >
      {(status === 'valid' || status === 'overridden') && (
        <Check className="size-2.5" strokeWidth={3} />
      )}
      {label}
    </span>
  );
}

const inputCls =
  'num h-8 w-full rounded-md border border-border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring/30';
const labelCls =
  'mb-1 block text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground';

function RecordForm({
  petId,
  requirement,
  onCancel,
  onRecorded,
}: {
  petId: string;
  requirement: VaccineRequirement;
  onCancel: () => void;
  onRecorded: (summary: PetVaccinationSummary) => void;
}) {
  const today = todayIso();
  const [administeredOn, setAdministeredOn] = useState(today);
  const [expiresOn, setExpiresOn] = useState(
    requirement.typicalValidityMonths ? addMonths(today, requirement.typicalValidityMonths) : '',
  );
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const changeAdministered = (v: string) => {
    setAdministeredOn(v);
    if (v && requirement.typicalValidityMonths) {
      setExpiresOn(addMonths(v, requirement.typicalValidityMonths));
    }
  };

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const { vaccination } = await createPetVaccination(petId, {
        vaccineId: requirement.vaccineId,
        administeredOn: administeredOn || null,
        expiresOn: expiresOn || null,
        notes: notes.trim() || null,
      });
      onRecorded({
        id: vaccination.id,
        vaccineId: requirement.vaccineId,
        vaccineName: requirement.vaccineName,
        expiresOn: vaccination.expiresOn,
        valid: true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record vaccination');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="mt-2 grid grid-cols-1 gap-2 rounded-md border border-border bg-surface p-3 sm:grid-cols-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      aria-label={`Record ${requirement.vaccineName}`}
    >
      <label className="block">
        <span className={labelCls}>Given on</span>
        <input
          type="date"
          value={administeredOn}
          onChange={(e) => changeAdministered(e.target.value)}
          className={inputCls}
        />
      </label>
      <label className="block">
        <span className={labelCls}>Expires on</span>
        <input
          type="date"
          value={expiresOn}
          onChange={(e) => setExpiresOn(e.target.value)}
          className={inputCls}
        />
      </label>
      <label className="block">
        <span className={labelCls}>Notes</span>
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Batch, vet, certificate…"
          className="h-8 w-full rounded-md border border-border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring/30"
        />
      </label>
      {error && <p className="text-xs text-destructive sm:col-span-3">{error}</p>}
      <div className="flex flex-wrap items-center gap-2 sm:col-span-3">
        <button
          type="submit"
          disabled={busy}
          className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground press disabled:opacity-50"
        >
          {busy && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
          Save vaccination
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground press"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function OverrideForm({
  petId,
  requirement,
  visitDate,
  onCancel,
  onGranted,
}: {
  petId: string;
  requirement: VaccineRequirement;
  visitDate: string;
  onCancel: () => void;
  onGranted: () => void;
}) {
  const [reason, setReason] = useState('');
  const [validUntil, setValidUntil] = useState(addDays(visitDate, 30));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!reason.trim()) {
      setError('A reason is required — it is recorded in the audit log.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await createVaccineOverride({
        petId,
        vaccineId: requirement.vaccineId,
        reason: reason.trim(),
        validUntil: validUntil || null,
      });
      onGranted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to grant override');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="mt-2 space-y-2 rounded-md border border-amber-300 bg-surface p-3 dark:border-amber-700"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      aria-label={`Override ${requirement.vaccineName}`}
    >
      <label className="block">
        <span className={labelCls}>Reason</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="e.g. titre test on file; vet letter received"
          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring/30"
        />
      </label>
      <label className="block sm:w-48">
        <span className={labelCls}>Valid until</span>
        <input
          type="date"
          value={validUntil}
          onChange={(e) => setValidUntil(e.target.value)}
          className={inputCls}
        />
      </label>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={busy}
          className="flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white press disabled:opacity-50"
        >
          {busy && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
          Grant override
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground press"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
